import { useState, useEffect } from "react";
import useLocalStorage from "react-use/lib/useLocalStorage";
import { addBreadcrumb, setExtra } from "@sentry/react";

import { getDietanamnezInfo } from "api/getLinkInfo";
import { LinkInfo } from "types/LinkInfo";
import { getContactToken } from "api/getContactToken";
import {
  getDietanamnez,
  updateDietanamnez,
  addDietanamnezItem,
  updateDietanamnezItem,
  removeDietanamnezItem,
  finishDietanamnez,
} from "api/dietanamnez";

import { Patient } from "types/Patient";
import { Allergen } from "types/Allergen";
import { Dietanamnez } from "types/Dietanamnez";
import { DietanamnezItem, DietanamnezItemForm } from "types/DietanamnezItem";
import { StateManagerType } from "./StateManagerType";
import { Screen } from "./allScreens";
import { UserType } from "types/UserType";
import { insertCss } from "utils/insertCss";

type SavedDietanamnezItem = DietanamnezItem & {
  key: string;
};

export const useDietanamnez = (
  id: string | null,
  userType: UserType
): StateManagerType => {
    let screens = {
    [UserType.LITE]: [
      "WELCOME",
      "DIETANAMNEZ",
      "DIETANAMNEZ_TILES",
      "SUPPLEMENT",
      "FINISH",
    ] as Screen[],
    [UserType.PRO]: [
      "WELCOME",
      "DIETANAMNEZ",
      "DIETANAMNEZ_TILES",
      "FINISH",
    ] as Screen[],
  }[userType];

  const isWelcomeScreenHidden = window.location.search.includes("welcome")

  if(isWelcomeScreenHidden) {
    screens = screens.slice(1)
  }

  type DietanamnezScreen = typeof screens[number];
  const [token, setToken] = useState<string>();
  const [screen, setScreen, removeScreen] = useLocalStorage<Screen>(
    `screen_${id}`
  );
  const [step = 0, setStep, removeStep] = useLocalStorage<number>(
    `step_${id}`,
    0
  );
  const [maxStep = 0, setMaxStep, removeMaxStep] = useLocalStorage<number>(
    `maxStep_${id}`,
    0
  )
  const [isError, setIsError] = useState<boolean>(false);
  const [linkInfo, setLinkInfo] = useState<LinkInfo>();
  const [dietanamnez, setDietanamnez] = useState<Dietanamnez>();
  const [dietanamnezItems = [], setDietanamnezItems] = useState<
    SavedDietanamnezItem[]
  >([]);

  const handleStart = async (id: string) => {
    try {
      const data = await getDietanamnezInfo(id);
      const token = await getContactToken({
        id: data.id,
        ret: data.ret,
        ContactArea: data.contactarea,
      });

      const { dietanamnez, dietanamnezItems } = await getDietanamnez(
        data.da_key,
        token
      );

      if (!screen) {
        setScreen(isWelcomeScreenHidden ? "DIETANAMNEZ" : "WELCOME")
      }

      setToken(token);

      setDietanamnez(dietanamnez);
      setDietanamnezItems(dietanamnezItems);

      setLinkInfo({
        consent: "",
        userName: data.io,
        manufacturers: data.manufacturers,
        authorName: dietanamnez.custom_фио_специалиста,
        custom: {
          custom_additional_data: {
            logo: data.custom_data?.logo,
            logo_welcome_size: data.custom_data?.logo_welcome_size,
            logo_final_size: data.custom_data?.logo_final_size,
            replacement_footer_copyright: data.custom_data?.replacement_footer_copyright!,
            selfregistration_report_type: data.custom_data?.selfregistration_report_type,
          }
        },
        logo: data.custom_data?.logo,
        link: data.link,
        country: data.country?.key || null,
      });

      if (data.custom_data?.css) {
        insertCss(data.custom_data?.css);
      }
    } catch (error) {
      console.error(error);
      setIsError(true);
    }
  };

  useEffect(() => {
    setExtra("STATE", {
      screen,
      step,
      maxStep,
      dietanamnez,
      dietanamnezItems,
    });
  });

  useEffect(() => {
    if (!linkInfo && id) {
      handleStart(id);
    }
  }, [id]);

  const addDietanamnezItemHandler = async (item: DietanamnezItem) => {
      const { key: itemKey } = await addDietanamnezItem(
          dietanamnez?.key!,
          token!,
          item
      );

      setDietanamnezItems((dietanamnezItems) =>
          dietanamnezItems.concat({
              ...item,
              key: itemKey,
          })
      );
  }

  const onPrevScreen = () => {
    const currentIndex = screens.indexOf(screen as DietanamnezScreen);
    setScreen(screens[currentIndex - 1]);
  };

  const onNextScreen = async () => {
    const currentIndex = screens.indexOf(screen as DietanamnezScreen);
    const currentSavedIndex = screens.indexOf(screen as DietanamnezScreen);
    const nextScreen = screens[currentIndex + 1];

    if (nextScreen === "FINISH") {
      await finishDietanamnez(dietanamnez?.key!, userType, token!);
    }

    if (currentIndex >= currentSavedIndex && !isError) {
      setScreen(nextScreen);
    }

    if (nextScreen === "FINISH") {
      window.addEventListener("unload", () => {
        removeScreen();
        removeStep();
        removeMaxStep();
      });
      window.parent.postMessage(
        JSON.stringify({ selftest_status: "COMPLETE", key: dietanamnez?.key! }),
        "*"
      );
    }
  };

  return {
    linkInfo,
    progress: 0.15,

    patient: {} as Patient,
    updatePatient: () => null,
    allergens: [] as Allergen[],
    setAllergens: () => null,
    screen,
    setScreen,
    dietanamnez,
    updateDietanamnez: async (values: Partial<Dietanamnez>) => {
      await updateDietanamnez(dietanamnez?.key!, token!, values);
      setDietanamnez({
        ...dietanamnez!,
        ...values,
      });
    },
    dietanamnezItems,
    setDietanamnezItem: async (key: string, values?: DietanamnezItemForm) => {
      addBreadcrumb({
        type: "debug",
        message: "Set DietanamnezItem",
        data: {
          key,
          ...(values || {}),
        },
      });

      const currentItem = dietanamnezItems.find(
        (item) => item.custom_блюдо === key
      );

      if (currentItem) {
        /*При повторном изменении значения плитки на вариант "никогда / иногда" для lite пользователя, удалить значение этой плитки*/
        if(!values && userType !== UserType.PRO) {
          // @ts-ignore
          await removeDietanamnezItem(currentItem.key, token!);
          setDietanamnezItems((dietanamnezItems) =>
              dietanamnezItems.filter((item) => item !== currentItem)
          );

          return;
        }

        /*При повторном изменении значения плитки на вариант, отличный от "никогда / иногда" для lite/pro пользователя, обновить значение плитки*/
        if(values) {
          await updateDietanamnezItem(currentItem.key, token!, {
            ...values,
            custom_добавить_исключить: 1
          });

          setDietanamnezItems((dietanamnezItems) =>
            dietanamnezItems.map((item) =>
              item === currentItem
                ? {
                  ...currentItem,
                  ...values,
                  custom_добавить_исключить: 1
                }
                : item
            )
          );
        }
        else {
          /*При повторном изменении значения плитки на вариант "никогда / иногда" для pro пользователя, обновить значение плитки*/
          await updateDietanamnezItem(currentItem.key, token!, {
            custom_количество: null,
            custom_частота_употребления: null,
            custom_добавить_исключить: 2
          });

          setDietanamnezItems((dietanamnezItems) =>
            dietanamnezItems.map((item) =>
              item === currentItem
                ? {
                  ...currentItem,
                  custom_количество: null,
                  custom_частота_употребления: null,
                  custom_добавить_исключить: 2
                }
                : item
            )
          );
        }

        return;
      }


      if (values) {
        /*При первом изменении значения плитки на вариант, отличный от "никогда / иногда" для lite/pro пользователя, занести значение плитки*/
        const item = {
          ...values,
          custom_добавить_исключить: 1,
          custom_блюдо: key,
        };

        await addDietanamnezItemHandler(item)
      } else {
        if(userType === UserType.PRO) {
          /*При первом изменении значение плитки на вариант "никогда / иногда" для pro пользователя, занести значение плитки*/
          const item = {
            custom_добавить_исключить: 2,
            custom_блюдо: key,
          };

          await addDietanamnezItemHandler(item)
        }
      }
    },
    step,
    setStep,
    maxStep,
    setMaxStep,
    isError,

    onPrevScreen,
    onNextScreen,
  };
};
